<template>
    <div class="cards-wrapper">
        <template v-for="(card, index) in cards" :key="index">
            <Card
                ref="refCards"
                v-if="card.grader"
                v-bind="getCardProps(card, index)"
                :forceBaseTier="mode == CARDS_MODE.FAN"
                :revealProgress="card.revealProgress"
                :revealAngle="card.revealAngle"
                :activePositionDelay="1.2"
            />
            <CardRaw ref="refCards" v-else v-bind="getCardProps(card, index)" />
        </template>
    </div>
</template>

<script setup>
    import { onMounted, ref, watch } from 'vue';

    import { usePane, useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import Card from './Card.vue';
    import CardRaw from './CardRaw.vue';
    import { useCardsMotion } from '~/composables/useCardsMotion';
    import { CARDS_MODE } from '~/core/constants';

    const props = defineProps({
        cards: { default: [] },
        cardIndex: { default: 0 },
    });

    const visible = ref(false);
    const active = ref(false);
    const motionActive = ref(false);
    const canReveal = ref(false);

    const mode = ref(CARDS_MODE.DEFAULT);
    const z = ref(0);

    const getCardProps = (card, index) => ({
        index,
        active:
            active.value && (mode.value == CARDS_MODE.DEFAULT ? index === props.cardIndex : true),
        visible: active.value,
        motionActive: motionActive.value,
        rotation: card.rotation,
        flip: card.flip,
        interactive: card.interactive,
        revealed: canReveal.value && card.revealed,
        zoom: card.zoom && card.rotation % 180 === 0,
        data: card,
    });

    const yGap = ref(0);
    const zGap = ref(1);

    const refCards = ref([]);

    const { object } = useThreeObject(null, { name: 'CardsWrapper' });
    const cardsMotion = useCardsMotion({ object });

    const addToParent = (parent) => parent.add(object);

    onMounted(() => {
        setCardPositions();
    });

    let tlVisible;
    const show = ({ delay = 0, delayPosition = 0.5 } = {}) => {
        if (visible.value) return;

        tlVisible?.kill();
        tlVisible = gsap
            .timeline({
                delay,
                onStart: () => {
                    visible.value = true;
                    active.value = true;
                },
                onComplete: () => {
                    canReveal.value = true;
                    motionActive.value = true;
                },
            })
            .add(() => setCardPositions(), 0)
            .fromTo(
                object.position,
                { z: 0 },
                { z: 0.8, duration: 1.2, ease: 'power2.inOut' },
                delayPosition
            );

        refCards.value.forEach((card, i) => tlVisible.add(() => card.show({ delay: i * 1 }), 0));

        return tlVisible;
    };

    const hide = ({ force = false } = {}) => {
        if (!visible.value) return;

        tlVisible?.kill();
        tlVisible = gsap
            .timeline({
                onStart: () => {
                    visible.value = false;
                    motionActive.value = false;
                },
                onComplete: () => {
                    active.value = false;
                    reset();
                },
            })
            .to(object.position, { z: 0, duration: 1.2, ease: 'power2.inOut' }, 0);

        refCards.value.forEach((card, i) => {
            const nCards = refCards.value.length;
            const stagger = mode.value == CARDS_MODE.FAN ? nCards * 0.08 - i * 0.08 : 0;

            tlVisible
                .to(card.object.position, { z: '-=2', duration: 1, ease: 'power2.inOut' }, stagger)
                .add(() => card.hide(), stagger);
        });

        if (force) tlVisible.progress(1);

        return tlVisible;
    };

    const setCardIndex = () => {
        motionActive.value = false;

        if (active.value) {
            const tl = gsap.timeline({ onComplete: () => (motionActive.value = true) });
            setCardPositions(tl);
        } else setCardPositions();
    };

    const setCardPositions = (tl) => {
        mode.value = CARDS_MODE.DEFAULT;
        refCards.value.forEach((card, index) => {
            const newIndex = index - props.cardIndex;
            const isPastFront = newIndex < 0;

            const y = isPastFront ? -3 : newIndex * yGap.value;
            const z = Math.max(newIndex, 0) * -zGap.value;

            if (tl) tl.to(card.object.position, { y, z, duration: 1.2, ease: 'power3.inOut' }, 0);
            else gsap.set(card.object.position, { y, z });
        });
    };

    let tlMode;
    const setMode = (mode) => {
        tlMode?.kill();
        switch (mode) {
            case CARDS_MODE.FAN:
                tlMode = cardsMotion.createFanMotion();
                break;
            default:
        }
    };

    const switchMode = (value) => (mode.value = value);
    watch(mode, setMode);

    const reset = () => {
        canReveal.value = false;
        active.value = false;

        mode.value = CARDS_MODE.DEFAULT;

        gsap.killTweensOf(object.position);
        gsap.killTweensOf(object.rotation);

        object.position.set(0, 0, 0);
        object.rotation.set(0, 0, 0);

        refCards.value.forEach((card) => {
            card.object.position.set(0, 0, 0);
            card.object.rotation.set(0, 0, 0);
            card.reset();
        });
    };

    const setProps = () => {
        refCards.value.forEach((card, i) => {
            card.object.position.y = i * yGap.value;
            card.object.position.z = i * -zGap.value;
        });
        object.position.z = z.value;
    };

    watch([zGap, yGap, z], setProps);
    watch(() => props.cardIndex, setCardIndex);

    usePane(
        [
            { name: 'zGap', value: zGap, options: { min: 0, max: 5, step: 0.01 } },
            { name: 'yGap', value: yGap, options: { min: 0, max: 2, step: 0.01 } },
            { name: 'z', value: z, options: { min: -2, max: 2, step: 0.01 } },
        ],
        { title: 'Cards' }
    );

    defineExpose({
        addToParent,
        show,
        hide,
        reset,
        switchMode,
    });
</script>
